import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';

interface Settings {
  openaiApiKey?: string;
  groqApiKey?: string;
  marketingPrompt?: string;
}

const marketingSchema = z.object({
  productService: z.string().min(1, 'Produto/serviço é obrigatório'),
  benefits: z.string().min(1, 'Benefícios principais são obrigatórios'),
  price: z.string().min(1, 'Preço é obrigatório'),
  paymentConditions: z.string().min(1, 'Condições de pagamento são obrigatórias'),
  promotions: z.string().optional(),
  competitiveAdvantages: z.string().min(1, 'Diferenciais da concorrência são obrigatórios'),
  audienceProfile: z.enum(['formal', 'informal']),
  aiProvider: z.enum(['openai', 'groq']),
});

type MarketingFormData = z.infer<typeof marketingSchema>;

const defaultPrompt = `Você é um especialista em marketing digital focado em campanhas de WhatsApp Marketing.
Com base nas informações fornecidas, crie modelos de mensagens personalizadas que possam ser utilizadas em listas de transmissão ou disparos segmentados.

INFORMAÇÕES DO PRODUTO/SERVIÇO:

Produto/Serviço: {productService}
Benefícios principais: {benefits}
Preço: {price}
Condições de pagamento: {paymentConditions}
Promoções: {promotions}
Diferenciais da concorrência: {competitiveAdvantages}
Perfil do público: {audienceProfile}

Por favor, forneça:
1. Mensagem de Apresentação (primeiro contato)
2. Mensagem Promocional (com foco em oferta/benefício)
3. Mensagem de Lembrete (reenvio curto e direto)
4. Mensagem com Conteúdo Visual (texto + sugestão de imagem/vídeo)
5. Mensagem em Formato de Áudio (roteiro curto e humanizado para narração)
6. Sugestões de Call-to-Action
7. Recomendações de Tom e Linguagem adequados ao público {audienceProfile}
8. Lista de ferramentas recomendadas (grátis ou de baixo custo) para criar imagens, vídeos e áudios.

Seja específico, prático e adapte o tom ao perfil do público.`;

export function MarketingAssistantPage() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<string>('');
  const [showResultModal, setShowResultModal] = useState(false);

  const { register, handleSubmit, formState: { errors, isSubmitting }, watch } = useForm<MarketingFormData>({
    resolver: zodResolver(marketingSchema),
    defaultValues: { audienceProfile: 'formal', aiProvider: 'openai' },
  });

  const watchedAiProvider = watch('aiProvider');

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const response = await fetch('/api/settings', {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        if (response.ok) {
          const data = await response.json();
          setSettings(data);
        }
      } catch (error) {
        console.error('Erro ao carregar configurações:', error);
        toast.error('Erro ao carregar configurações');
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  const onSubmit = async (data: MarketingFormData) => {
    if (!settings) {
      toast.error('Configurações não carregadas');
      return;
    }
    const apiKey = data.aiProvider === 'openai' ? settings.openaiApiKey : settings.groqApiKey;
    if (!apiKey) {
      toast.error(`Chave API do ${data.aiProvider === 'openai' ? 'OpenAI' : 'Groq'} não configurada`);
      return;
    }
    setGenerating(true);
    setResult('');
    try {
      const prompt = settings.marketingPrompt || defaultPrompt;
      const formattedPrompt = prompt
        .replace(/\{productService\}/g, data.productService)
        .replace(/\{benefits\}/g, data.benefits)
        .replace(/\{price\}/g, data.price)
        .replace(/\{paymentConditions\}/g, data.paymentConditions)
        .replace(/\{promotions\}/g, data.promotions || 'Não especificado')
        .replace(/\{competitiveAdvantages\}/g, data.competitiveAdvantages)
        .replace(/\{audienceProfile\}/g, data.audienceProfile === 'formal' ? 'formal e profissional' : 'descontraído e próximo');

      const response = await fetch('/api/marketing/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
        body: JSON.stringify({ prompt: formattedPrompt, provider: data.aiProvider, apiKey }),
      });

      if (response.ok) {
        const res = await response.json();
        setResult(res.result || '');
        setShowResultModal(true);
        toast.success('Estratégia de marketing gerada com sucesso!');
      } else {
        const err = await response.json();
        toast.error(err.error || 'Erro ao gerar estratégia de marketing');
      }
    } catch (error) {
      console.error('Erro ao gerar estratégia:', error);
      toast.error('Erro ao gerar estratégia de marketing');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Carregando configurações...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header title="Assistente Marketing" subtitle="Gere estratégias de campanha com IA" />

      <div className="p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-3">🤖 Como usar o Assistente Marketing</h3>
            <div className="text-blue-800 space-y-2">
              <p>
                Preencha os campos abaixo com informações detalhadas sobre seu produto ou serviço.
                A IA usará o prompt padrão definido em Configurações para gerar uma estratégia completa.
                Use as variáveis no prompt: {'{productService}'}, {'{benefits}'}, {'{price}'}, {'{paymentConditions}'}, {'{promotions}'}, {'{competitiveAdvantages}'}, {'{audienceProfile}'}.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Escolha a IA</label>
                <div className="grid grid-cols-2 gap-4">
                  <label className={`relative flex items-center p-4 border rounded-lg cursor-pointer ${watchedAiProvider === 'openai' ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-gray-400'}`}>
                    <input type="radio" value="openai" {...register('aiProvider')} className="sr-only" />
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center"><span className="text-green-600 font-bold">O</span></div>
                      <div><div className="font-medium text-gray-900">OpenAI</div><div className="text-sm text-gray-500">ChatGPT</div></div>
                    </div>
                  </label>
                  <label className={`relative flex items-center p-4 border rounded-lg cursor-pointer ${watchedAiProvider === 'groq' ? 'border-yellow-500 bg-yellow-50' : 'border-gray-300 hover:border-gray-400'}`}>
                    <input type="radio" value="groq" {...register('aiProvider')} className="sr-only" />
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center"><span className="text-yellow-600 font-bold">G</span></div>
                      <div><div className="font-medium text-gray-900">Groq</div><div className="text-sm text-gray-500">Modelos rápidos</div></div>
                    </div>
                  </label>
                </div>
              </div>

              <div>
                <label htmlFor="productService" className="block text-sm font-medium text-gray-700 mb-1">Produto/Serviço *</label>
                <textarea id="productService" {...register('productService')} rows={3} placeholder="Descreva o produto ou serviço..." className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                {errors.productService && <p className="text-red-500 text-sm mt-1">{errors.productService.message}</p>}
              </div>
              <div>
                <label htmlFor="benefits" className="block text-sm font-medium text-gray-700 mb-1">Benefícios principais *</label>
                <textarea id="benefits" {...register('benefits')} rows={4} placeholder="Principais benefícios..." className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                {errors.benefits && <p className="text-red-500 text-sm mt-1">{errors.benefits.message}</p>}
              </div>
              <div>
                <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">Preço *</label>
                <input id="price" type="text" {...register('price')} placeholder="Ex: R$ 299,90" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                {errors.price && <p className="text-red-500 text-sm mt-1">{errors.price.message}</p>}
              </div>
              <div>
                <label htmlFor="paymentConditions" className="block text-sm font-medium text-gray-700 mb-1">Condições de pagamento *</label>
                <textarea id="paymentConditions" {...register('paymentConditions')} rows={2} placeholder="À vista, parcelado..." className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                {errors.paymentConditions && <p className="text-red-500 text-sm mt-1">{errors.paymentConditions.message}</p>}
              </div>
              <div>
                <label htmlFor="promotions" className="block text-sm font-medium text-gray-700 mb-1">Promoções (opcional)</label>
                <textarea id="promotions" {...register('promotions')} rows={2} placeholder="Descontos, frete grátis..." className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label htmlFor="competitiveAdvantages" className="block text-sm font-medium text-gray-700 mb-1">Diferenciais da concorrência *</label>
                <textarea id="competitiveAdvantages" {...register('competitiveAdvantages')} rows={3} placeholder="O que torna único?" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
                {errors.competitiveAdvantages && <p className="text-red-500 text-sm mt-1">{errors.competitiveAdvantages.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Perfil do público *</label>
                <div className="grid grid-cols-2 gap-4">
                  <label className={`flex items-center p-4 border rounded-lg cursor-pointer ${watch('audienceProfile') === 'formal' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
                    <input type="radio" value="formal" {...register('audienceProfile')} className="sr-only" />
                    <div className="font-medium text-gray-900">Formal</div>
                  </label>
                  <label className={`flex items-center p-4 border rounded-lg cursor-pointer ${watch('audienceProfile') === 'informal' ? 'border-green-500 bg-green-50' : 'border-gray-300'}`}>
                    <input type="radio" value="informal" {...register('audienceProfile')} className="sr-only" />
                    <div className="font-medium text-gray-900">Informal</div>
                  </label>
                </div>
              </div>
              <button type="submit" disabled={isSubmitting || generating} className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                {generating ? <> <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" /> Gerando estratégia...</> : <> 🚀 Gerar Estratégia de Marketing </>}
              </button>
            </form>
          </div>
        </div>
      </div>

      {showResultModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">📋 Estratégia de Marketing Gerada</h3>
              <button onClick={() => setShowResultModal(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">{result}</div>
            </div>
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex gap-3">
              <button onClick={() => { navigator.clipboard.writeText(result); toast.success('Copiado!'); }} className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700">📋 Copiar</button>
              <button onClick={() => setShowResultModal(false)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Fechar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
